/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { Model } from '@adobe/aem-spa-page-model-manager';

import {
  DCA_CONSTANTS,
  HYBRID_CONFIG_CONSTANTS,
  DEFAULT_ACCEPT_LANGUAGE,
  ACCEPT_LANGUAGE_TEST_REGEX,
  PI_DATA as dataLayerFilterList,
  AEP_COOKIE_PREFIX,
} from './constants';
import { mockSessionData } from '../mocks/sessionData';
import { logger } from './logger';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { NonEmptyString } from './helpers.types';
import { GlobalPageProps, SessionData } from './session';
import { IPageModel, IReturnTypeOFGetMboxParam, IReturnTypeOFTargetMboxParam, MboxExtractionContext } from '../types';

const { NEXT_PUBLIC_DYNAMIC_ROUTING_URL, SESSION_APP_CALL_URL, NEXT_PUBLIC_FEATURE_FLAG_URL } = process.env;
export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

export const getProcessEnvs = () => {
  const processENV = process.env && { ...process.env };
  delete processENV['NEXT_AEM_AUTH'];
  delete processENV['NEXT_PUBLIC_AEM_AUTH'];
  if (canUseDOM) {
    const envTag = document.getElementById('__SERVERENV__');
    if (envTag) {
      const envObject = JSON.parse(envTag.innerHTML);
      return envObject;
    }
    return processENV;
  }
  return processENV;
};

export async function getPageModel(
  path: string,
  locale: string,
  sessionID: string,
  applicationName: string,
  selector: string,
  isAuthenticated = false,
  eaaPageIndicator = '',
  PREPROCESSOR_EXPERIENCE_SEGMENT: Array<string>,
  productSpecificSeoUrl = null,
  requestId: string
) {
  const { log, pLog } = global.loggerInstance('ModelCall');
  const { ROUTING_CONFIG, EXPERIENCE_SEGMENT, EXPERIENCE_SEGMENT_AUTHENTICATED } = HYBRID_CONFIG_CONSTANTS;
  locale = locale?.replace('_', '-');
  const pagePath = path.split('?')[0];

  const getCMSTemplateReqBody: {
    requestType: string;
    seoUrl: string;
    localeKey: string;
    experienceSegment: Array<string>;
    applicationName: string;
    sessionToken: string;
    selector?: string;
    eaaPageIndicator?: string;
    selectors?: string;
  } = {
    requestType: ROUTING_CONFIG,
    seoUrl: productSpecificSeoUrl ? productSpecificSeoUrl : pagePath,
    localeKey: locale,
    experienceSegment: PREPROCESSOR_EXPERIENCE_SEGMENT
      ? PREPROCESSOR_EXPERIENCE_SEGMENT
      : isAuthenticated
      ? [...EXPERIENCE_SEGMENT_AUTHENTICATED]
      : [...EXPERIENCE_SEGMENT],
    applicationName: applicationName,
    sessionToken: sessionID,
  };

  if (selector) {
    getCMSTemplateReqBody['selectors'] = selector;
  }
  if (eaaPageIndicator) {
    getCMSTemplateReqBody['experienceSegment'] = [...getCMSTemplateReqBody['experienceSegment'], eaaPageIndicator];
  }

  let pageModel = {};
  const apiStartTime = new Date().getTime();
  try {
    log.debug(`NEXT_PUBLIC_DYNAMIC_ROUTING_URL: ${NEXT_PUBLIC_DYNAMIC_ROUTING_URL}`);
    log.debug(`getCMSTemplateReqBody: ${JSON.stringify(getCMSTemplateReqBody)}`);
    const response = await axios.post(`${NEXT_PUBLIC_DYNAMIC_ROUTING_URL}`, getCMSTemplateReqBody, {
      headers: {
        'x-request-id': requestId,
      },
    });
    if (response) {
      pageModel = response.data.data;
    } else {
      log.error('AEM model failure:', response);
      pageModel = {};
    }
  } catch (err) {
    log.error('API call failed:', err);
    //returing page model and logging error
    return pageModel;
  }
  pLog.log('API call performance timing', apiStartTime, new Date().getTime());
  return pageModel;
}
//converts ":items" format to "cqItems" format.
export function transformToCQ(propKey: string) {
  const tempKey = propKey.substring(1);

  return 'cq' + tempKey.substring(0, 1).toUpperCase() + tempKey.substring(1);
}

/**
 * Normalizes trackingProperties to ensure consistent data types
 * Converts string "true"/"false" to boolean true/false
 */
const normalizeTrackingProperties = (trackingProps: any): any => {
  if (!trackingProps || typeof trackingProps !== 'object') {
    return trackingProps;
  }

  const normalized = { ...trackingProps };

  // Automatically normalize any property with string "true"/"false" to boolean
  Object.keys(normalized).forEach(prop => {
    if (normalized[prop] === 'true') {
      normalized[prop] = true;
    } else if (normalized[prop] === 'false') {
      normalized[prop] = false;
    }
    // Keep other values as-is (undefined, null, numbers, actual booleans, etc.)
  });

  return normalized;
};

/**
 * Recursively normalizes trackingProperties in nested objects
 */
const deepNormalizeTrackingProperties = (obj: any): any => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepNormalizeTrackingProperties);
  }

  const normalized = { ...obj };

  // Normalize trackingProperties at this level
  if (normalized.trackingProperties) {
    normalized.trackingProperties = normalizeTrackingProperties(normalized.trackingProperties);
  }

  // Also handle referTrackingProperties (for RAF components)
  if (normalized.referTrackingProperties) {
    normalized.referTrackingProperties = normalizeTrackingProperties(normalized.referTrackingProperties);
  }

  // Recursively process nested objects
  Object.keys(normalized).forEach(key => {
    if (typeof normalized[key] === 'object' && normalized[key] !== null) {
      normalized[key] = deepNormalizeTrackingProperties(normalized[key]);
    }
  });

  return normalized;
};

//To format mock model in local dev, in other envs this formatting is taken care by OOTB fetchModel utility
export const respGridUtil = (item: Model, options: { normalizeTracking?: boolean } = {}) => {
  if (!item || !Object.keys(item).length) {
    return { cqPath: '' };
  }

  const keys = Object.getOwnPropertyNames(item);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props: any = {};

  keys.forEach((key: string) => {
    const propKey = key.startsWith(':') ? transformToCQ(key) : key;
    props[propKey] = item[key as keyof Model] || '';
  });

  // Apply tracking normalization if requested
  if (options.normalizeTracking) {
    return deepNormalizeTrackingProperties(props);
  }

  return props;
};

export async function getSessionData(
  sessionIDVal: string,
  isLocalDev: boolean,
  isAuthorMode: boolean,
  requestId: string
) {
  return new Promise((resolve, reject) => {
    let sessionData;
    let sessionHeader: any = {};
    const { log, pLog } = global.loggerInstance('SessionCall');
    const sessionGetCallUrl =
      SESSION_APP_CALL_URL || useClientEnvVarsStore.getState().envVarsObject['SESSION_GET_CALL_URL_CLIENT'];

    if (isLocalDev || isAuthorMode) {
      resolve({ sessionData: mockSessionData, sessionHeader: '' });
    } else {
      const apiStartTime = new Date().getTime();
      log.debug(JSON.stringify({ SESSION_APP_CALL_URL: sessionGetCallUrl, sessionIDVal }));
      axios
        .get(`${sessionGetCallUrl}`, {
          headers: {
            Cookie: `sessionID=${sessionIDVal}`,
            'Cache-Control': 'no-store, no-cache, private',
            'x-request-id': requestId,
          },
        })
        .then(resp => {
          sessionHeader = resp?.headers;
          sessionData = resp?.data;
          resolve({ sessionData, sessionHeader });
        })
        .catch(e => {
          log.error('Error with session route!', e);
          reject('session');
        });
      pLog.log(`API call performance timing start: ${apiStartTime} end: ${new Date().getTime()}`);
    }
  });
}

export async function getSessionDataWithParams(
  preHeader: Record<string, string>,
  isLocalDev: boolean,
  isAuthorMode: boolean
) {
  return new Promise((resolve, reject) => {
    let sessionData;
    let sessionHeader: any = {};
    const { log, pLog } = global.loggerInstance('SessionCall');

    const sessionBody = { keys: process.env['NEXT_PUBLIC_SESSION_KEYS'] };

    if (isLocalDev || isAuthorMode) {
      resolve({ sessionData: mockSessionData, sessionHeader: '' });
    } else {
      const apiStartTime = new Date().getTime();
      log.debug(JSON.stringify({ SESSION_APP_CALL_URL, token: preHeader }));
      axios
        .post(`${SESSION_APP_CALL_URL}`, sessionBody, {
          headers: preHeader,
        })
        .then(resp => {
          sessionHeader = resp?.headers;
          sessionData = resp?.data;
          resolve({ sessionData, sessionHeader });
        })
        .catch(e => {
          log.error('Error with session route!', e);
          reject('session');
        });
      pLog.log(`API call performance timing start: ${apiStartTime} end: ${new Date().getTime()}`);
    }
  });
}

export async function getV1SessionData(
  sessionIDVal: string,
  isLocalDev: boolean,
  isAuthorMode: boolean,
  searchKeys: string
) {
  return new Promise((resolve, reject) => {
    let sessionData;
    let sessionHeader: any = {};
    const { log } = logger({ requestID: '', sessionID: '' })('SessionCall');

    const sessionApiUrl =
      process.env['SESSION_APP_GET_CALL_V1_URL'] ||
      useClientEnvVarsStore.getState().envVarsObject['SESSION_APP_GET_CALL_V1_URL'];
    if (isLocalDev || isAuthorMode) {
      log.debug(`Detected local or Author mode, using mock session`);
      resolve({ sessionData: mockSessionData, sessionHeader: '' });
    } else {
      const apiStartTime = new Date().getTime();
      log.debug(`sessionApiUrl: ${sessionApiUrl}`);
      log.debug(`session token: ${sessionIDVal}`);
      axios
        .post(
          `${sessionApiUrl}`,
          {
            keys: searchKeys,
          },
          {
            headers: {
              Cookie: `sessionID=${sessionIDVal}`,
            },
          }
        )
        .then(resp => {
          sessionHeader = resp?.headers;
          sessionData = resp?.data;
          resolve({ sessionData, sessionHeader });
        })
        .catch(e => {
          log.debug('Error with session route!', e);
          reject('session');
        });
      log.debug('API call performance timing', apiStartTime, new Date().getTime());
    }
  });
}

/**
 * Populates the PI data layer with values from cookies based on the provided dataLayerFilterList.
 *
 * @param {boolean} isAcdl - A flag indicating whether to store the data in sessionStorage (true) or in window.dataLayer (false).
 * @returns {Record<string, string>} - The populated PI object if isAcdl is true, otherwise an empty object.
 */
export function populatePIdataLayer(sessionData: SessionData = {}, hasOwnDataLayer = false) {
  const isEnableWebSDK = useClientEnvVarsStore.getState()?.envVarsObject?.['NEXT_PUBLIC_ENABLE_WEBSDK'];
  const ENABLE_ACDL_HARNESS = useClientEnvVarsStore.getState()?.envVarsObject?.['ENABLE_ACDL'];

  /**
   * Retrieves the value of a specified cookie.
   *
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string | null} - The value of the cookie, or null if not found.
   */

  function getCookieValue(name: string): string | null {
    const cookies = cookieString.split('; ');

    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) {
        return value;
      }
    }

    return null;
  }

  const cookieString = document.cookie;
  const piObj: Record<string, string> = {};
  const memState: string = window?.dataLayer?.['memState']?.toString()?.toLowerCase() ?? '';
  const isAnonymousUser: boolean = memState !== 'authenticated' && memState !== 'remembered';

  // Iterate over each key-value pair in dataLayerFilterList
  for (const [key, val] of Object.entries(dataLayerFilterList)) {
    // Split the value by commas to handle multiple cookie names
    const cookieNames = val.split(',');
    // Iterate through each cookie name and assign the first found value to piObj
    // for acdl off scenario sessiondata shold be read from window object, for on scenario - should be from session store
    const sessionDataObj = isEnableWebSDK ? sessionData : window.sessionData;
    for (const cookieName of cookieNames) {
      const value = getCookieValue(cookieName.trim());
      // For altCustId & mr_id_alternate first we need to pick it from sessionData,
      // if not then pick from cookie

      // TODO: clean up code after current consumer ID cookie issue is fixed
      // shop page sessionData is without cacheData added fallback for home/shop/cobrand
      // (!isAnonymousUser || isEnableWebSDK) - isAnonymousUser check will beused for flag off scenario here
      // isEnableWebSDK will be ised for flag on.
      //for acdl true, session data will always be updated so anonymous check is not required.
      if (
        (key === 'altCustId' || key === 'mr_id_alternate') &&
        (sessionDataObj?.cacheData?.data?.consumerID || sessionDataObj?.data?.consumerID) &&
        (!isAnonymousUser || isEnableWebSDK)
      ) {
        piObj[key] = sessionDataObj?.cacheData?.data?.consumerID ?? sessionDataObj?.data?.consumerID;
      } else if (value) {
        piObj[key] = value;
      }
    }
  }

  // Store the data in sessionStorage if isAcdl is true, otherwise in window.dataLayer
  if (isEnableWebSDK) {
    const oldDataLayer = hasOwnDataLayer
      ? JSON.parse(JSON.stringify(window.dataLayer || {}))
      : JSON.parse(window.sessionStorage.getItem('oldDataLayer') || '{}');
    // if harness flag is disbled then only we should update it in session storage
    String(ENABLE_ACDL_HARNESS) !== 'true' &&
      window.sessionStorage.setItem('oldDataLayer', JSON.stringify({ ...oldDataLayer, ...piObj }));
    return piObj;
  } else {
    window.dataLayer = { ...window.dataLayer, ...piObj };
    return {};
  }
}

export function getDataLayerScript(dataLayer: { data: any; mvpOffersData: string }) {
  return `var dataLayer = ${dataLayer?.data ? JSON.stringify(dataLayer?.data[0]) : '{}'}; var mvpOffers = ${
    dataLayer?.mvpOffersData && dataLayer?.mvpOffersData !== 'null' ? dataLayer?.mvpOffersData : '{}'
  };`;
}

export function getSessionDataScript(sessionData: Record<string, string>) {
  return `var sessionData = ${sessionData ? JSON.stringify(sessionData) : '{}'};`;
}

export async function getFeatureFlagData() {
  const { log } = global.loggerInstance('FeatureFlag');
  log.debug(`API url: ${NEXT_PUBLIC_FEATURE_FLAG_URL}`);
  return new Promise((resolve, reject) => {
    axios
      .get(`${NEXT_PUBLIC_FEATURE_FLAG_URL}`)
      .then(res => {
        resolve(res.data);
      })
      .catch(e => {
        log.error('Error with feature flag route!', e);
        reject('featureflag');
      });
  });
}

export const mtGC = (cname: string): string => {
  const name = cname + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      const cnameValue = c.substring(name.length, c.length);
      try {
        return decodeURIComponent(cnameValue);
      } catch {
        return cnameValue;
      }
    }
  }
  return '';
};

export const mtGCV = (cname: string): string => {
  const x: Array<string> = mtGC(cname).split('|');
  let i;
  for (i = 0; i < x.length; i++) {
    if (x[i] === DCA_CONSTANTS.MCMID_COOKIE) {
      return x[i + 1];
    }
  }
  return '';
};

export const cleanObject = (input: object): object => {
  if (typeof input === 'object' && input !== null) {
    if (Array.isArray(input)) {
      return input?.map(cleanObject)?.filter(item => item !== null && item !== undefined && item?.toString() !== '');
    }

    return Object.fromEntries(
      Object.entries(input)
        .map(([key, val]) => [key, cleanObject(val)])
        .filter(([k, v]) => v !== null && v !== undefined && v !== '' && k)
    );
  }

  return input;
};

/**
 * Validates accept-language header against a regex
 * @param header string
 * @returns boolean
 */
export const isValidAcceptLanguageHeader = (header: string): boolean => {
  const regex = ACCEPT_LANGUAGE_TEST_REGEX;
  return regex.test(header);
};

/**
 * Pulls accept-language header out of request object and validates it.
 * @param req Request object with (presumably) some heades on it
 * @returns string Valid accept-language header
 */
export const processAcceptLanguage = (locale: string): string => {
  // Remove underscore, remove spaces, split and take whaever's before the comma.
  let parsedAccLang = locale?.replace(/\s+/g, '')?.replace('_', '-').split(';')[0].split(',')[0] as NonEmptyString;
  // If a region designation is present, attempt to uppercase it.
  if (parsedAccLang?.length === 5) {
    parsedAccLang = `${parsedAccLang.split('-')[0]}-${parsedAccLang.split('-')[1].toUpperCase()}` as NonEmptyString;
  }
  // If parsed value truthy and valid, return, else return default.
  return parsedAccLang && isValidAcceptLanguageHeader(parsedAccLang)
    ? parsedAccLang
    : (DEFAULT_ACCEPT_LANGUAGE as NonEmptyString);
};

/**
 * @description Prop resolver for Server and Client
 * @param name Key of the process env variable to access
 * @returns {string|undefined} return env variable form either paeg props or process
 */
export const getEnvProp = (name: string): string | undefined => {
  if (typeof window !== 'undefined') {
    return JSON.parse(window.__NEXT_DATA__?.props?.['pageProps']?.['serverENV'] || '{}')?.[name] || '';
  }
  return process.env[name] || '';
};
/**
 *
 * @param pageModel global level pageModel as Object structure
 * @param mboxExtractionContext optional override to inject an allow-list `mboxList` for a specific componentType and filter out other mboxes
 * @returns array of mboxParameter names, their respective cell codes, compName & path as well
 * @description along with mboxParamter names & data, it also added one more key, to save dca Response
 * which will be helpful for easier mapping
 *
 * this new version will iterate over arrays as well
 */

export const getMboxParamters = (pageModel: IPageModel, mboxExtractionContext?: MboxExtractionContext) => {
  const mboxParameters: IReturnTypeOFGetMboxParam[] = [];
  let enableBannerTriggerLogic = false;

  const normalizedContext = (() => {
    if (!mboxExtractionContext || typeof mboxExtractionContext.componentType !== 'string') return undefined;
    const mboxList = Array.from(
      new Set(
        (Array.isArray(mboxExtractionContext.mboxList) ? mboxExtractionContext.mboxList : []).filter(
          (s): s is string => typeof s === 'string' && s.length > 0
        )
      )
    );
    if (mboxList.length === 0) return undefined;
    return { componentType: mboxExtractionContext.componentType, mboxList };
  })();

  const checkInNestedObject = (node: any, nodeName = '', path = '') => {
    if (!node) return;

    if (Array.isArray(node)) {
      node.forEach((item, index) =>
        checkInNestedObject(item, nodeName, path.length > 0 ? `${path}.${index}` : `${index}`)
      );
      return;
    } else if (typeof node === 'object') {
      // If this node matches the extraction context, skip scanning its subtree and use the allow-list mboxes.
      if (normalizedContext) {
        const componentType = (node?.[':type'] as unknown) ?? (node?.cqType as unknown);
        if (componentType === normalizedContext.componentType) {
          const currentPath = path;
          normalizedContext.mboxList.forEach(mboxName => {
            mboxParameters.push({
              compKey: nodeName,
              path: currentPath,
              mboxName,
              cellCodes: '',
              dcaResponse: null,
              uxlResponse: null,
              enableOfferVariation: undefined,
            });
          });
          return;
        }
      }

      if (Object.prototype.hasOwnProperty.call(node, 'mboxParameter')) {
        const mboxName = node?.['mboxParameter'] as string;
        const cellCodes = (
          Object.prototype.hasOwnProperty.call(node, 'cellCodes') ? node?.['cellCodes'] : ''
        ) as string;
        mboxParameters.push({
          compKey: nodeName,
          path: path,
          mboxName: mboxName,
          cellCodes: cellCodes,
          dcaResponse: null, // this key is will save existing modelJSON of component + uxlResponse
          uxlResponse: null, // this key is created to save only uxlResponse which will be used for container components
          enableOfferVariation: node?.['enableOfferVariation'], // this flag is used for ABTesting of HeroBanner 3:1
        });
      }
      if (Object.prototype.hasOwnProperty.call(node, 'enableTriggerLogic') && !enableBannerTriggerLogic) {
        enableBannerTriggerLogic = node['enableTriggerLogic'];
      }
      Object.keys(node)
        .filter(key => typeof node[key] === 'object' && node[key] !== null)
        .forEach(key => checkInNestedObject(node[key], key, `${path.length > 0 ? path + '.' : ''}${key}`));
    }
  };
  checkInNestedObject(pageModel);
  return { mboxParameters, enableBannerTriggerLogic };
};

export function getAepCookieName(name: string) {
  return [AEP_COOKIE_PREFIX, DCA_CONSTANTS.orgId?.replace('@', '_'), name].join('_');
}

// TODO Replace with harness feature flag.
// Keeping query params for now to enable its testing along with v1
export const isV2UxlEnabled = () => {
  if (!canUseDOM) {
    return false;
  }
  const envVarsObject = useClientEnvVarsStore.getState().envVarsObject;

  return !!(
    (envVarsObject?.['NEXT_PUBLIC_ENABLE_WEBSDK'] && envVarsObject?.['NEXT_PUBLIC_ENABLE_DCA_V2'] === 'true') ||
    new URLSearchParams(window?.location?.search)?.get('useV2Uxl') === 'true'
  );
};

export function getLocale(): string {
  return (
    window?.__NEXT_DATA__?.props?.['pageProps']?.currentLocale?.replace('-', '_') ||
    window?.sessionData?.locale?.replace('-', '_') ||
    'en_US'
  );
}

export const getAlloyMboxParamters = (pageModel: IPageModel): IReturnTypeOFTargetMboxParam[] => {
  const mboxParameters: IReturnTypeOFTargetMboxParam[] = [];
  const checkInNestedObject = (innerObject: any, path = '') => {
    for (const key in innerObject) {
      // check if innerObject[key] is an object or not
      if (Object.prototype.toString.call(innerObject[key]) === '[object Object]') {
        checkInNestedObject(innerObject[key], path?.length > 0 ? path + '.' + key : key);
      }

      if (
        Object.prototype.toString.call(innerObject[key]) === '[object Object]' &&
        Object.prototype.hasOwnProperty.call(innerObject?.[key], 'targetMbox')
      ) {
        const mboxName = innerObject[key]?.['targetMbox'] as string;
        mboxParameters.push({
          path: path + '.' + key,
          targetMbox: mboxName,
          alloyResponse: null,
        });
      }
    }
  };
  checkInNestedObject(pageModel);
  return mboxParameters;
};

export const getIsACDLenabledFlag = (pageProps: GlobalPageProps): boolean => {
  const serverComputedFlag = pageProps?.model?.IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL;

  return (
    String(getEnvProp('NEXT_PUBLIC_ENABLE_WEBSDK')) === 'true' &&
    String(pageProps?.model?.enableWebSDK) === 'true' &&
    (serverComputedFlag ?? true)
  );
};

export const isACDLEnabledLocale = (locales: string[], currentLocale = '') => {
  if (typeof window !== 'undefined') {
    return locales?.includes(
      String(window.__NEXT_DATA__?.props?.['pageProps']?.['model']?.['language'])
        ?.replace('_', '-')
        ?.toLocaleLowerCase()
    );
  } else if (currentLocale) {
    return locales?.includes(String(currentLocale)?.replace('_', '-')?.toLocaleLowerCase());
  } else return false;
};
